import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";

import { Button, FormControl, MenuItem, Select, Stack, Switch, TextField } from "@mui/material";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { BranchModel, IccUser } from "../../../../../models/AdminPageModels";

interface AdminIccBranches {
    data: IccBranchesData;
    error: IError;
}
interface IccBranchesData {
    branches: BranchModel[];
}
interface AdminDbUsers {
    data: DbUsersData;
    error: IError;
}
interface DbUsersData {
    users: IccUserModel[];
}
interface IccUserModel {
    id: number;
    userName: string;
    userPass: string;
    branchID: number;
    boothID: string;
    enabled: boolean;
}

interface EditIccUserRequest {
    id: number;
    username: string;
    password: string;
    branchId: number;
    boothId: string;
    enabled: boolean;
}
interface EditIccUserResponse {
    data: EditIccUserData;
    error: IError;
}
interface EditIccUserData {
    status: string;
}

interface EditIccUserProps {
    editUser: IccUser | null;
}

const EditIccUser: FC<EditIccUserProps> = ({ editUser }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [userId, setUserId] = useState<number>(0);
    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [selectedBranch, setSelectedBranch] = useState<number>(0);
    const [boothId, setBoothId] = useState<string>("");
    const [enabled, setEnabled] = useState<boolean>(true);

    const [users, setUsers] = useState<IccUserModel[]>([]);
    const [branches, setBranches] = useState<BranchModel[]>([]);

    useEffect(() => {
        if (branches.length === 0)
            GetBranches();
        if (users.length === 0) {
            GetUsers().then((temp: IccUserModel[] | undefined) => {
                if (editUser === null)
                    return;
                if (temp === undefined)
                    return;

                var user = null;

                for (var i = 0; i < temp.length; i++) {
                    if (temp[i].id === editUser.id) {
                        user = temp[i]
                        break;
                    }
                }

                if (user === null)
                    return;

                setUserId(user.id);
                setUsername(user.userName);
                setPassword(user.userPass);
                setSelectedBranch(user.branchID);
                setBoothId(user.boothID);
                setEnabled(user.enabled);
            });
        }
    }, [editUser])

    const GetBranches = async () => {
        try {
            const response: AdminIccBranches = await api.get("api/Admin/GetICCBranches", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setBranches(response.data.branches)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const GetUsers = async () => {
        try {
            const response: AdminDbUsers = await api.get("api/Admin/GetDbUsers", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setUsers(response.data.users);
                return response.data.users;
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    };

    const EditClient = async () => {
        if (userId === 0 || username === "" || password === "" || selectedBranch === 0 || boothId === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: EditIccUserRequest = {
            id: userId,
            username: username,
            password: password,
            branchId: selectedBranch,
            boothId: boothId,
            enabled: enabled
        }

        try {
            const response: EditIccUserResponse = await api.post("api/Admin/EditIccUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Edited!", { variant: "success" })
                // clear fields
                setUserId(0);
                setUsername("");
                setPassword("");
                setSelectedBranch(0);
                setBoothId("");
                setEnabled(true);

                // get new users from db
                GetUsers();
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SetUserId(event: any) {
        var value: number = event.target.value;
        setUserId(value);
        var user = null;

        for (var i = 0; i < users.length; i++) {
            if (users[i].id === value) {
                user = users[i]
                break;
            }
        }

        if (user === null)
            return;

        setUsername(user.userName);
        setPassword(user.userPass);
        setSelectedBranch(user.branchID);
        setBoothId(user.boothID);
        setEnabled(user.enabled);
    }

    const isFormValid = username !== "" && password !== "" && selectedBranch !== 0 && boothId !== "";

    return (
        <Stack>
            {/* Users */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Users: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={userId}
                        onChange={SetUserId}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select user"}
                        </MenuItem>
                        {users.map((user, index) =>
                            <MenuItem
                                key={index}
                                value={user.id}>
                                {user.userName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue: any) => { setUsername(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Password */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Password: </p>
                <TextField
                    value={password}
                    onChange={(newValue: any) => { setPassword(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Branch */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>BranchId: </p>
                <FormControl sx={{ minWidth: 245 }}>
                    <Select
                        value={selectedBranch}
                        onChange={(newValue: any) => { setSelectedBranch(newValue.target.value); }}
                        size="small"
                        defaultValue={-1}>
                        <MenuItem
                            value={0}>
                            {"Select branch"}
                        </MenuItem>
                        {branches.map((type, index) =>
                            <MenuItem
                                key={index}
                                value={type.id}>
                                {type.branchName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* BoothId */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>BoothId: </p>
                <TextField
                    value={boothId}
                    onChange={(newValue: any) => { setBoothId(newValue.target.value) }}
                    sx={{ minWidth: 245 }}
                    size="small" />
            </Stack>

            {/* Enabled */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Enabled: </p>
                <Switch
                    checked={enabled}
                    onChange={(newValue: any) => { setEnabled(newValue.target.checked) }}
                    size="small" />
            </Stack>

            <Button
                variant="outlined"
                onClick={() => EditClient()}
                sx={{ width: '82px' }}
                disabled={!isFormValid}>
                Edit
            </Button>
        </Stack>
    )
}

export default EditIccUser;